import React, { useCallback, useEffect, useState } from "react";
import { MapPinned, Plus, RefreshCw, Trash2 } from "lucide-react";
import api from "../api";

const S = `
.sms-wrap{color:#0f172a}
.sms-head{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:16px}
.sms-title{display:flex;align-items:center;gap:10px;font-size:22px;font-weight:800;color:#0f172a}
.sms-sub{font-size:13px;color:#475569}
.sms-actions{display:flex;gap:8px;flex-wrap:wrap}
.sms-btn,.sms-btn-light{min-height:42px;border-radius:12px;padding:10px 14px;font-size:13px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:8px}
.sms-btn{border:1px solid #2563eb;background:#2563eb;color:#fff}
.sms-btn:disabled{opacity:.65;cursor:not-allowed}
.sms-btn-light{border:1px solid #cbd5e1;background:#fff;color:#0f172a}
.sms-grid{display:grid;grid-template-columns:340px 1fr;gap:16px}
.sms-card{background:#fff;border:1px solid #dbe4f0;border-radius:18px;box-shadow:0 14px 34px rgba(15,23,42,.08)}
.sms-card-h{padding:16px 18px;border-bottom:1px solid #e2e8f0;font-size:15px;font-weight:800;color:#0f172a}
.sms-card-b{padding:16px 18px}
.sms-field{display:flex;flex-direction:column;gap:6px;margin-bottom:12px}
.sms-label{font-size:12px;font-weight:800;color:#0f172a;text-transform:uppercase;letter-spacing:.04em}
.sms-input{width:100%;min-height:44px;border:1.5px solid #94a3b8;border-radius:12px;padding:10px 12px;background:#fff;color:#000;font-size:14px;outline:none;box-shadow:0 4px 10px rgba(37,99,235,.06)}
.sms-input:focus{border-color:#2563eb;box-shadow:0 0 0 4px rgba(37,99,235,.12)}
.sms-help{font-size:12px;color:#64748b}
.sms-table-wrap{overflow:auto}
.sms-table{width:100%;border-collapse:collapse}
.sms-table th,.sms-table td{padding:12px 14px;border-bottom:1px solid #eef2f7;text-align:left;color:#0f172a;font-size:13px}
.sms-table th{background:#f8fafc;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.04em}
.sms-name{font-weight:700}
.sms-empty{padding:30px 16px;text-align:center;color:#64748b}
.sms-row-action{border:1px solid #fecaca;background:#fff1f2;color:#b91c1c;border-radius:10px;padding:8px 10px;font-size:12px;font-weight:700;cursor:pointer}
.sms-msg{margin-bottom:12px;padding:10px 12px;border-radius:12px;font-size:13px;font-weight:600}
.sms-msg.ok{background:#ecfdf5;border:1px solid #a7f3d0;color:#166534}
.sms-msg.err{background:#fef2f2;border:1px solid #fecaca;color:#b91c1c}
@media(max-width:900px){.sms-grid{grid-template-columns:1fr}}
`;

export default function StateMasterSettings() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/states");
      setRows(res?.data?.data || []);
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.message || "Failed to load state list.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!name.trim()) {
      setError("State name is required.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api.post("/admin/states", { name: name.trim(), code: code.trim().toUpperCase() || undefined });
      setName("");
      setCode("");
      setMessage("State added successfully.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add state.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setError("");
    setMessage("");
    try {
      await api.delete(`/admin/states/${id}`);
      setMessage("State deleted successfully.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete state.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <style>{S}</style>
      <div className="sms-wrap">
        <div className="sms-head">
          <div>
            <div className="sms-title"><MapPinned size={22} /> State Master</div>
            <div className="sms-sub">Manage India state and union territory names used in the sales customer form.</div>
          </div>
          <div className="sms-actions">
            <button className="sms-btn-light" onClick={load} disabled={loading}>
              <RefreshCw size={15} /> Refresh
            </button>
          </div>
        </div>
        {message ? <div className="sms-msg ok">{message}</div> : null}
        {error ? <div className="sms-msg err">{error}</div> : null}
        <div className="sms-grid">
          <div className="sms-card">
            <div className="sms-card-h">Add New State</div>
            <div className="sms-card-b">
              <div className="sms-field">
                <label className="sms-label">State Name</label>
                <input className="sms-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter state or union territory name" />
              </div>
              <div className="sms-field">
                <label className="sms-label">State Code</label>
                <input className="sms-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Optional code like AP, TS, DL" maxLength={20} />
              </div>
              <div className="sms-help">This list is used by the sales customer add/edit popup dropdown.</div>
              <div style={{ marginTop: 14 }}>
                <button className="sms-btn" onClick={handleAdd} disabled={saving}>
                  <Plus size={15} /> {saving ? "Adding..." : "Add State"}
                </button>
              </div>
            </div>
          </div>
          <div className="sms-card">
            <div className="sms-card-h">Available States</div>
            <div className="sms-table-wrap">
              {loading ? (
                <div className="sms-empty">Loading states...</div>
              ) : rows.length === 0 ? (
                <div className="sms-empty">No states available.</div>
              ) : (
                <table className="sms-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Code</th>
                      <th style={{ width: 120 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td className="sms-name">{row.name}</td>
                        <td>{row.code || "-"}</td>
                        <td>
                          <button className="sms-row-action" onClick={() => handleDelete(row.id)} disabled={deletingId === row.id}>
                            <Trash2 size={13} style={{ marginRight: 6 }} />
                            {deletingId === row.id ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
